import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Tag, ShoppingCart, Receipt, Percent } from 'lucide-react';
import apiClient from '@/services/api';
import type { SalesDetailReport as SalesDetailDto } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Sales Detail: one row per closed ticket, plus grand totals. */
export default function SalesDetailReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<SalesDetailDto>({
    queryKey: ['sales-detail', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getSalesDetail(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const t = data?.totals;
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const fmtTs = (s: string | null) => (s ? format(parseISO(s), 'MMM dd HH:mm') : '—');

  const sections = (): ReportSection[] => [
    {
      heading: 'Tickets',
      headers: ['Ticket', 'Closed', 'Order Type', 'Server', 'Table', 'Guests', 'Subtotal', 'Tax', 'Discount', 'Service', 'Delivery', 'Tip', 'Total'],
      rows: [
        ...(data?.tickets ?? []).map((r) => [
          String(r.posTicketId ?? r.ticketNumber ?? '—'), fmtTs(r.closedAt), r.orderType ?? '—', r.serverName ?? '—', r.tableName ?? '—',
          r.guests ?? 0, formatCurrency(r.subtotal), formatCurrency(r.tax), formatCurrency(r.discount),
          formatCurrency(r.serviceCharge), formatCurrency(r.deliveryCharge), formatCurrency(r.tip), formatCurrency(r.total),
        ]),
        ...(t ? [[
          'Totals', '', '', '', '',
          Number(t.covers ?? 0).toLocaleString(),
          formatCurrency(t.subtotal), formatCurrency(t.tax), formatCurrency(t.discount),
          formatCurrency(t.serviceCharge), formatCurrency(t.deliveryCharge), formatCurrency(t.tip), formatCurrency(t.total),
        ]] : []),
      ],
    },
  ];
  const kpis = (): [string, string][] => [
    ['Orders', Number(t?.orders ?? 0).toLocaleString()],
    ['Discount', formatCurrency(t?.discount ?? 0)],
    ['Tax', formatCurrency(t?.tax ?? 0)],
    ['Total', formatCurrency(t?.total ?? 0)],
  ];

  return (
    <ReportFrame
      title="Sales Detail"
      description="Every closed ticket in the range with its financial breakdown."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={!data || (data.tickets?.length ?? 0) === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`sales-detail-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Sales Detail', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Sales Detail Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Orders" value={Number(t?.orders ?? 0).toLocaleString()} icon={ShoppingCart} palette="blue" />
        <KPICard label="Discount" value={formatCurrency(t?.discount ?? 0)} icon={Tag} palette="green" />
        <KPICard label="Tax" value={formatCurrency(t?.tax ?? 0)} icon={Percent} palette="red" />
        <KPICard label="Total" value={formatCurrency(t?.total ?? 0)} icon={Receipt} palette="purple" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Tickets ({Number(t?.orders ?? 0).toLocaleString()})</h3>
        <SimpleTable
          headers={['Ticket', 'Closed', 'Type', 'Server', 'Table', 'Guests', 'Subtotal', 'Tax', 'Disc.', 'Service', 'Delivery', 'Tip', 'Total']}
          rows={(data?.tickets ?? []).map((r) => [
            String(r.posTicketId ?? r.ticketNumber ?? '—'), fmtTs(r.closedAt), r.orderType ?? '—', r.serverName ?? '—', r.tableName ?? '—',
            r.guests ?? 0, formatCurrency(r.subtotal), formatCurrency(r.tax), formatCurrency(r.discount),
            formatCurrency(r.serviceCharge), formatCurrency(r.deliveryCharge), formatCurrency(r.tip), formatCurrency(r.total),
          ])}
          footer={t ? [
            'Totals', '', '', '', '',
            Number(t.covers ?? 0).toLocaleString(),
            formatCurrency(t.subtotal), formatCurrency(t.tax), formatCurrency(t.discount),
            formatCurrency(t.serviceCharge), formatCurrency(t.deliveryCharge), formatCurrency(t.tip), formatCurrency(t.total),
          ] : undefined}
        />
      </div></div>
    </ReportFrame>
  );
}
